import { INewsContentService } from '../services/i-news-content-service';
import { NewsTranslationContentRequest, NewsTranslationContentResponse } from '../dto/news-content';
import { MakeReactive, noopMakeReactive } from '../ui/make-reactive';

interface TranslationCacheEntry {
	content: NewsTranslationContentResponse;
	timestamp: number;
}

interface NewsTranslationStoreState {
	cache: Map<string, TranslationCacheEntry>;
	isLoading: boolean;
	error: string;
}

/**
 * Cache TTL: 10 minutes (articles change less frequently)
 */
const CACHE_TTL = 10 * 60 * 1000;

/**
 * Store for managing news article content with caching
 */
export class NewsTranslationStore {
	private state: NewsTranslationStoreState;
	private service: INewsContentService;

	constructor(
		service: INewsContentService,
		makeReactive: MakeReactive<unknown> = noopMakeReactive
	) {
		this.service = service;
		this.state = makeReactive({
			cache: new Map<string, TranslationCacheEntry>(),
			isLoading: false,
			error: ''
		}) as NewsTranslationStoreState;
	}

	private isCacheValid(entry: TranslationCacheEntry): boolean {
		return Date.now() - entry.timestamp < CACHE_TTL;
	}

	/**
	 * Get news article content by translation ID
	 */
	async getNewsContent(translationId: string): Promise<NewsTranslationContentResponse> {
		// Check cache first
		const cached = this.state.cache.get(translationId);
		if (cached && this.isCacheValid(cached)) {
			return cached.content;
		}

		// Fetch from service
		try {
			this.state.isLoading = true;
			this.state.error = '';

			const request: NewsTranslationContentRequest = {
				translationId
			};

			const response = await this.service.getContent(request);

			// Update cache
			const entry: TranslationCacheEntry = {
				content: response,
				timestamp: Date.now()
			};
			this.state.cache.set(translationId, entry);

			return response;

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error loading article content';
			this.state.error = errorMessage;
			throw error;
		} finally {
			this.state.isLoading = false;
		}
	}

	/**
	 * Get cached content (if available)
	 */
	getCachedContent(translationId: string): NewsTranslationContentResponse | null {
		const cached = this.state.cache.get(translationId);

		if (cached && this.isCacheValid(cached)) {
			return cached.content;
		}

		return null;
	}

	/**
	 * Preload content (for prefetching next/previous articles)
	 */
	async preloadContent(translationId: string): Promise<void> {
		// Only preload if not already cached
		if (!this.getCachedContent(translationId)) {
			try {
				await this.getNewsContent(translationId);
			} catch (error) {
				// Silently fail for preloading
				console.warn('Preload failed for:', translationId, error);
			}
		}
	}

	/**
	 * Clear cache for specific article
	 */
	clearCache(translationId: string): void {
		this.state.cache.delete(translationId);
	}

	/**
	 * Clear all cache
	 */
	clearAllCache(): void {
		this.state.cache.clear();
	}

	/**
	 * Check if currently loading
	 */
	isLoading(): boolean {
		return this.state.isLoading;
	}

	/**
	 * Get last error
	 */
	getError(): string {
		return this.state.error;
	}
}



